import {Download} from 'lucide-react'
import React, {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'

import {downloadAttendanceRecordsCsv} from '../../modes/attendance/exportAttendanceRecordsCsv'
import {AttendanceParsedContent} from '../../modes/attendance/parseAttendanceAnswer'

const INITIAL_VISIBLE_ROWS = 5
const LOAD_MORE_BATCH = 5

type AttendanceRecordsTableProps = {
  content: AttendanceParsedContent
  theme: 'light' | 'dark'
  embedded?: boolean
  hideCsv?: boolean
}

const AttendanceRecordsTable: React.FC<AttendanceRecordsTableProps> = ({
  content,
  theme,
  embedded = false,
  hideCsv = false,
}) => {
  const intl = useIntl()
  const {columns, rows} = content
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ROWS)

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ROWS)
  }, [content])

  const handleDownloadCsv = useCallback(() => {
    downloadAttendanceRecordsCsv(columns, rows)
  }, [columns, rows])

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, rows.length))
  }, [rows.length])

  if (!columns.length || !rows.length) return null

  const visibleRows = rows.slice(0, visibleCount)
  const showLoadMore = rows.length > INITIAL_VISIBLE_ROWS && visibleCount < rows.length

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
  const headerBg = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
  const textColor = theme === 'dark' ? '#ffffff' : '#212529'
  const buttonMuted = theme === 'dark' ? 'rgba(255,255,255,0.85)' : '#6c757d'

  const buttonStyle = {
    fontSize: '0.75rem',
    color: buttonMuted,
    borderColor,
  }

  return (
    <div className={`attendance-records-table-wrap${embedded ? '' : ' mt-2 mb-1'}`}>
      <div style={{overflowX: 'auto'}}>
        <table
          className='table table-sm table-bordered mb-0'
          style={{
            fontSize: '0.85rem',
            color: textColor,
            borderColor,
          }}
        >
          <thead>
            <tr style={{backgroundColor: headerBg}}>
              {columns.map((col) => (
                <th
                  key={col}
                  scope='col'
                  style={{
                    color: textColor,
                    borderColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {columns.map((col) => (
                  <td
                    key={col}
                    style={{
                      color: textColor,
                      borderColor,
                      verticalAlign: 'middle',
                    }}
                  >
                    {row[col] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='d-flex justify-content-between align-items-center mt-2 gap-2'>
        <div>
          {showLoadMore ? (
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={handleLoadMore}
              style={buttonStyle}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.LOAD_MORE'})}
            </button>
          ) : null}
        </div>
        {!hideCsv ? (
          <button
            type='button'
            className='btn btn-sm btn-light d-inline-flex align-items-center gap-1'
            onClick={handleDownloadCsv}
            style={buttonStyle}
            aria-label={intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DOWNLOAD_CSV'})}
          >
            <Download size={14} aria-hidden />
            {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.DOWNLOAD_CSV'})}
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default AttendanceRecordsTable
